import React, { useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import VideoPlayButton from 'components/essentials/VideoPlayButton';
import JBImage from 'components/essentials/JBImage';
import { useMediaQuery } from 'react-responsive';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import useIsBrowser from 'hooks/useIsBrowser';
import NewToggleCTALinkButton from 'components/essentials/NewToggleCTALinkButton';
import MobileOnly from '../MobileOnly';
import TabletAndDesktopOnly from '../TabletAndDesktopOnly';

const Banner = props => {
  const [isPlaying, setPlaying] = useState(true);
  const videoRef = useRef(null);

  const toggleVideoPlayback = useCallback(() => {
    if (!isPlaying) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  }, [isPlaying]);

  const videoPlaybackOnEnd = useCallback(() => {
    setPlaying(false);
    videoRef.current.load();
    videoRef.current.pause();
  }, []);
  const isMobile = useMediaQuery({
    query: '(max-width: 768px)',
  });
  const isBrowser = useIsBrowser();
  const renderDefaultInNewConsumerApp = isNewConsumerApp && !isBrowser;
  const isDeviceBelow426px = useMediaQuery({
    query: '(max-width: 425px)',
  });
  const isBelow426px = renderDefaultInNewConsumerApp
    ? true
    : isDeviceBelow426px;
  const isDeviceBelow2000px = useMediaQuery({
    query: '(max-width: 1999px)',
  });
  const isBelow2000px = renderDefaultInNewConsumerApp
    ? true
    : isDeviceBelow2000px;
  if (!props.config || !Object.keys(props.config).length) return null;
  const getImageQualityFactor = () => {
    if (isBelow426px) {
      return 20;
    }
    if (isBelow2000px) {
      return 40;
    }
    return 60;
  };
  const {
    bannerSection,
    videoBlock,
    textContentSection,
    imageComponent,
    buttonSection,
  } = props.config;
  const {
    textColor,
    textOrientation,
    aboveTitle,
    title,
    belowTitle,
  } = textContentSection;
  const {
    image: {
      desktopSrc = '',
      mobileSrc = '',
      showOverlay = 0,
      minHeight = '65vh',
    },
    video: { videoLink = '', videoImageSrc = '' },
  } = bannerSection;
  const overlayOpacity = showOverlay ? parseInt(showOverlay, 10) : null;
  const orientationValue = textOrientation || 'left';
  const orientationLowerCase = orientationValue
    .toLowerCase()
    .split(' ')
    .join('-');
  const AboveTitleTag = aboveTitle.tag;
  const TitleTag = title.tag;
  const BelowTitleTag = belowTitle.tag;
  return (
    <div
      className={classNames(
        'flex font-sans mx-auto my-0 relative w-full px-5 py-12 md:px-[3.333vw] md:py-8 lg:p-20 print:hidden',
        {
          'w-full': imageComponent?.size !== 'Full Page',
          'max-w-[1400px]': imageComponent?.size === 'Full Page',
          'flex-row': orientationLowerCase !== 'right',
          'content-start': orientationLowerCase === 'left',
          'flex-row-reverse': orientationLowerCase === 'right',
          'items-start': orientationLowerCase === 'top-left',
          'items-start flex-row-reverse': orientationLowerCase === 'top-right',
          'items-start justify-center': orientationLowerCase === 'top-center',
          'items-end': orientationLowerCase === 'bottom-left',
          'items-end flex-row-reverse': orientationLowerCase === 'bottom-right',
          'items-end justify-center': orientationLowerCase === 'bottom-center',
          'justify-center': orientationLowerCase === 'center',
        }
      )}
      style={{
        minHeight: `${minHeight && minHeight.value ? minHeight.value : '65vh'}`,
      }}
    >
      <div className="h-full left-0 absolute top-0 w-full">
        {overlayOpacity ? (
          <div
            className="h-full left-0 absolute top-0 w-full bg-black [content:''] z-[1]"
            style={{
              opacity: `${overlayOpacity ? overlayOpacity / 100 : 0}`,
            }}
          />
        ) : null}
        {videoBlock && videoBlock.enabled.value === 'Enabled' ? (
          <div className="ImageBanner__video-block">
            <TabletAndDesktopOnly>
              <video
                ref={videoRef}
                controls={false}
                onEnded={videoPlaybackOnEnd}
                className="max-[767px]:hidden h-full left-0 max-w-full object-cover object-center absolute top-0 w-full"
                key={videoLink}
                autoPlay
                muted
              >
                <source src={videoLink} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div
                className={classNames(
                  'max-[767px]:hidden absolute bottom-4 right-4 z-[1200] w-[140px] transition-[opacity_.2s_ease-in-out] opacity-100'
                )}
              >
                <VideoPlayButton
                  isPlaying={isPlaying}
                  styling="outline-button"
                  sizes="sm"
                  onClick={toggleVideoPlayback}
                >
                  {isPlaying ? 'Pause' : 'Play'}
                </VideoPlayButton>
              </div>
            </TabletAndDesktopOnly>
            <MobileOnly>
              <JBImage
                src={videoImageSrc || null}
                sizes="100vw"
                className="md:hidden h-full left-0 max-w-full object-cover object-center absolute top-0 w-full"
                alt=""
              />
            </MobileOnly>
          </div>
        ) : (
          <JBImage
            src={isMobile ? mobileSrc : desktopSrc}
            sizes="100vw"
            className="h-full left-0 max-w-full object-cover object-center absolute top-0 w-full"
            alt=""
            quality={getImageQualityFactor()}
          />
        )}
      </div>
      <div
        className={classNames(
          'content-center flex flex-col max-w-[50%] z-[1] justify-center',
          {
            'max-[767px]:max-w-none':
              orientationLowerCase === 'left' ||
              orientationLowerCase === 'right' ||
              orientationLowerCase === 'center',
            'items-end':
              orientationLowerCase === 'top-right' ||
              orientationLowerCase === 'bottom-right' ||
              orientationLowerCase === 'right',
            'text-center':
              orientationLowerCase === 'top-center' ||
              orientationLowerCase === 'bottom-center' ||
              orientationLowerCase === 'center',
            'items-start':
              orientationLowerCase === 'bottom-left' ||
              orientationLowerCase === 'top-left' ||
              orientationLowerCase === 'left',
          }
        )}
        style={{ color: textColor || 'white' }}
      >
        {aboveTitle?.text && (
          <AboveTitleTag
            className={classNames(
              'font-bold uppercase mt-0 mx-0 mb-[.5em] text-inherit',
              aboveTitle.size
            )}
          >
            {aboveTitle.text}
          </AboveTitleTag>
        )}
        {title.text && (
          <TitleTag
            className={classNames(
              'm-0 tracking-[.0125rem] font-bold text-inherit',
              title.size
            )}
          >
            {title.text}
          </TitleTag>
        )}
        {belowTitle?.text && (
          <BelowTitleTag
            className={classNames(
              'mt-3 mx-0 mb-6 font-normal text-inherit',
              belowTitle.size,
              {
                'text-center justify-items-center': orientationLowerCase.endsWith(
                  'center'
                ),
                'text-left justify-items-start': orientationLowerCase.endsWith(
                  'left'
                ),
                'text-right justify-items-end': orientationLowerCase.endsWith(
                  'right'
                ),
              }
            )}
          >
            {belowTitle?.text && (
              <div
                className="[&_ul]:list-disc [&_ol]:list-[auto] [&_ul]:list-inside [&_ol]:list-inside [&_ul]:pl-4 [&_ol]:pl-4"
                dangerouslySetInnerHTML={{ __html: belowTitle.text }}
              />
            )}
          </BelowTitleTag>
        )}
        <div
          className={classNames(
            'flex max-[767px]:flex-col flex-row items-center gap-4',
            {
              'justify-center':
                orientationLowerCase === 'center' ||
                orientationLowerCase === 'top-center' ||
                orientationLowerCase === 'bottom-center',
            }
          )}
        >
          {Object.entries(buttonSection).map(([key, buttonProps]) => (
            <NewToggleCTALinkButton {...buttonProps} key={key} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Banner;
